import { type Entity } from './entity';

type Relation<T> = {
    [K in keyof T]: T[K] extends string[]
        ? K extends `${string}Ids`
            ? K
            : never
        : never;
}[keyof T];

export class Repository<T extends Entity> {
    index = new Map<string, number>();
    protected items: T[] = [];

    constructor(private changeCallback: () => void) {}

    add(item: T): void {
        const newLength = this.items.push(item);
        this.index.set(item.id, newLength - 1);
        this.changeCallback();
    }

    addAll(items: T[]): void {
        this.items = [...items];
        this.rebuildIndex();
        this.changeCallback();
    }

    getAll(): T[] {
        return this.items;
    }

    getById(id?: string): T | undefined {
        if (!id) {
            return undefined;
        }
        const index = this.index.get(id);
        if (index === undefined) {
            return undefined;
        }
        const item = this.items[index];
        if (!item) {
            return undefined;
        }
        return item;
    }

    getAllByIDs(ids: string[]): T[] {
        const indexes = ids
            .map((id) => this.index.get(id))
            .filter((index) => index !== undefined);
        return indexes.map((index) => this.items[index]) as T[];
    }

    update(item: T): void {
        const index = this.index.get(item.id);
        if (index === undefined) {
            throw new Error(`Item with id ${item.id} not found`);
        }
        this.items = [
            ...this.items.slice(0, index),
            item,
            ...this.items.slice(index + 1),
        ];
        this.changeCallback();
    }

    remove(id: string): void {
        const index = this.index.get(id);
        if (index === undefined) {
            throw new Error(`Item with id ${id} not found`);
        }
        this.items = [
            ...this.items.slice(0, index),
            ...this.items.slice(index + 1),
        ];
        this.rebuildIndex();
        this.changeCallback();
    }

    removeRelation(id: string, relationKey: Relation<T>): void {
        this.items.forEach((entity) => {
            const relation = this.getRelation(relationKey, entity);
            const index = relation.indexOf(id);
            if (index !== -1) {
                relation.splice(index, 1);
            }
        });
    }

    clear(): void {
        this.items = [];
        this.index.clear();
    }

    toJSON(): object[] {
        return this.items.map((item) => item.toJSON());
    }

    private getRelation(key: Relation<T>, entity: T): string[] {
        return entity[key] as string[];
    }

    private rebuildIndex(): void {
        this.index.clear();
        this.items.forEach((item, index) => {
            this.index.set(item.id, index);
        });
    }
}
